import { type PlayerRatingAllResponse, RankingType } from '@on3/api';
import { On3IconLock } from '@on3/icons';
import { On3 } from '@on3/ui-lib/components/Svg';
import { useAuth } from '@on3/ui-lib/index';
import { slugify } from '@on3/ui-lib/utils/slugify';
import clsx from 'clsx';
import { Link } from 'components/Link';
import { StarRating } from 'components/UI/StarRating/StarRating';

import styles from './Rankings.module.scss';

interface IPlayerRankingItem {
  service?: PlayerRatingAllResponse;
  athlete?: { name?: string };
}

export const RankingsItem = ({ service, athlete }: IPlayerRankingItem) => {
  const { user } = useAuth();
  const { link, type, rating, stars, stateRank, overallRank, positionRank } =
    service || {};

  const isOn3 = type?.toLowerCase() === 'on3';
  const isTransferPortalRank =
    service?.rankingType === RankingType.TransferPortal;

  const linkSport = slugify(service?.sport || 'football');
  const linkYear = service?.rankingYear || new Date().getFullYear();
  const linkPosition = service?.positionAbbr?.toLocaleLowerCase() || 'qb';
  const linkState = service?.stateAbbr?.toLocaleLowerCase() || 'al';
  const baseLink = isTransferPortalRank
    ? `/transfer-portal/industry/${linkSport}/${linkYear}/`
    : `/db/rankings/player/${linkSport}/${linkYear}/`;
  const overallLink = link ?? baseLink;
  const positionLink = `${baseLink}?position=${linkPosition}`;
  const stateLink = `${baseLink}?state=${linkState}`;

  const utmParams = new URLSearchParams({
    utm_source: 'player_rankings_module',
    utm_medium: 'web',
    utm_campaign: `${slugify(athlete?.name ?? 'athlete')}`,
    utm_content: 'lock',
  });

  return (
    <li className={styles.rankingsItem}>
      <Link
        className={clsx(styles.serviceItem, {
          [styles.locked]: !user?.has,
        })}
        fallback={'div'}
        href={!isOn3 && user?.has ? link : ''}
      >
        <div className={styles.name}>
          {isOn3 ? (
            <On3 className={styles.on3Icon} />
          ) : (
            <span>{service?.type}</span>
          )}
        </div>

        {!user?.has ? (
          <Link
            className={styles.subscribe}
            href={`/subscribe/?${utmParams.toString()}`}
          >
            <On3IconLock />
            <span>Unlock</span>
          </Link>
        ) : (
          <>
            <span className={styles.rating}>
              {rating
                ? type?.toLowerCase() === 'rivals'
                  ? rating?.toFixed(1)
                  : rating
                : '-'}
            </span>

            <div className={styles.stars}>
              <StarRating stars={stars} />
            </div>

            <div
              className={clsx(styles.industryRankingsValues, {
                [styles.transferPortal]: isTransferPortalRank,
              })}
            >
              <Link className={styles.rank} href={isOn3 ? overallLink : ''}>
                {overallRank || '-'}
              </Link>
              <Link className={styles.rank} href={isOn3 ? positionLink : ''}>
                {positionRank || '-'}
              </Link>
              {!isTransferPortalRank && (
                <Link className={styles.rank} href={isOn3 ? stateLink : ''}>
                  {stateRank || '-'}
                </Link>
              )}
            </div>
          </>
        )}
      </Link>
    </li>
  );
};
